﻿import React, { useEffect, useRef, useState } from "react";
import styles from "./AutonomousAP.module.css";
import { getResponsiveValue } from "../../util_glob";
import debounce from "lodash/debounce";
import SlideNav from "./SlideNav";
import { ScrollDownAnimatedArrows } from "../ScrollDownAnimatedArrows/ScrollDownAnimatedArrows";
import InStoryNav  from "../marketing/InStoryNav";
const AutonomousAP: React.FC = () => {
    const containerRef = useRef<HTMLElement | null>(null);
    const lenisRef = useRef<any>(null);
    const rafId = useRef<number | null>(null);
    const currentActive = useRef<HTMLElement | null>(null);
    const [activeSlide, setActiveSlide] = useState(-1);
    const [isHiddenSlideOne, setIsHiddenSlideOne] = useState(false);
    const [hitTop, setHitTop] = useState<boolean>(true);

    const marginpx  = useRef<number>(getResponsiveValue({
        height: window.innerHeight,
        minHeight: 200,
        maxHeight: 2000,
        minValue: 40,
        maxValue: 100,
    })); // extra margin for intersection observer
    const interactionthreshold1  = useRef<number>(getResponsiveValue({
        height: window.innerHeight,
        minHeight: 400,
        maxHeight: 3000,
        minValue: 0.15,
        maxValue: 0.2
    }))
    const interactionthreshold2  = useRef<number>(getResponsiveValue({
        height: window.innerHeight,
        minHeight: 400,
        maxHeight: 3000,
        minValue: 0.4,
        maxValue: 0.5
    }))
    const interactionthreshold3  = useRef<number>(getResponsiveValue({
        height: window.innerHeight,
        minHeight: 400,
        maxHeight: 3000,
        minValue: 0.6,
        maxValue: 0.9
    }))
    useEffect(() => {
        import.meta.env.DEV && console.log("activeSlide changed to ", activeSlide);
    }, [activeSlide]);

    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;

        const box = document.querySelector('.scrollable');
        box?.classList.add('hide-scrollbar');

        let mounted = true;
        let lenisScrollHandler: any = null;
        let snapTimeout: number | null = null;

        (async () => {
            try {
                const mod = await import("lenis");
                const Lenis = (mod && (mod.default || mod)) as any;
                if (!mounted) return;
                lenisRef.current = new Lenis({
                    smooth: true,
                    lerp: 0.1,
                    duration:0.7,
                    smoothWheel: true,
                    smoothTouch: true,
                    syncTouch: true,
                    autoRaf: true,
                    touchInertiaExponent: 12
                });
            } catch (err) {
                console.warn("Lenis failed to load, falling back to native scroll.", err);
            }
        })();

        const slidesForObserver = root.querySelectorAll("." + styles.slide);

        const findNearestToCenter = () => {
            const activeEntries: NodeListOf<HTMLElement> = root.querySelectorAll("." + styles.active);
            let el = activeEntries.length === 1 ? activeEntries[0] : null;
            if (el) return el;
        };

        const centerSlide = (el: HTMLElement) => {
            if (!el) return;
                try {
                    // duration in ms-ish — adjust for snappiness
                    import.meta.env.DEV && console.log("centering slide ", el);
                    lenisRef.current?.scrollTo(el, {
                        offset: -window.innerHeight / 2 + el.offsetHeight / 2, // centers the element vertically
                        duration: 0.5,
                        lock: true,
                        onComplete: () => { lenisRef }
                    })

                } catch (e) {
                    import.meta.env.DEV && console.log("‹errr_cntr_sld›", e);
                }
        };

        // scroll event handler: debounce and snap when user stops
        const onLenisScroll = debounce((newActive: HTMLElement) => {
            const nearest = findNearestToCenter();
            centerSlide(newActive);
        }, 1);

        const handleResize = () => {
            marginpx.current = getResponsiveValue({
                height: window.innerHeight,
                minHeight: 800,
                maxHeight: 3000,
                minValue: 0,
                maxValue: 100
            })


            interactionthreshold1.current  = getResponsiveValue({
                height: window.innerHeight,
                minHeight: 400,
                maxHeight: 3000,
                minValue: 0.3,
                maxValue: 0.4
            })
            interactionthreshold2.current  = getResponsiveValue({
                height: window.innerHeight,
                minHeight: 400,
                maxHeight: 3000,
                minValue: 0.7,
                maxValue: 0.1
            })
            interactionthreshold3.current  = getResponsiveValue({
                height: window.innerHeight,
                minHeight: 400,
                maxHeight: 3000,
                minValue: 0.6,
                maxValue: 0.9
            })
        };

        window.addEventListener("scroll", () => {
            if (window.scrollY < 40){
                if (hitTop != true) setHitTop(true)
                if (activeSlide != 0) setActiveSlide(0);
                if (isHiddenSlideOne != true) setIsHiddenSlideOne(true);
                currentActive.current = null;
            } else {
                if (hitTop == true) setHitTop(false)
                if (isHiddenSlideOne == true) setIsHiddenSlideOne(false);
            }
        });

        window.addEventListener("resize", handleResize);
        const observer = new IntersectionObserver((entries) => {
            // find intersecting entries
            const visibleEntries = entries.filter((e) => e.isIntersecting && e.intersectionRatio >= getResponsiveValue({
                height: window.innerHeight,
                minHeight: 400,
                maxHeight: 3000,
                minValue: 0.2,
                maxValue: 0.5,
            }));

            if (visibleEntries.length > 0) {
                // pick the most visible one
                const mostVisible = visibleEntries.reduce((prev, curr) =>
                    prev.intersectionRatio > curr.intersectionRatio ? prev : curr
                );
                const newActive = mostVisible.target as HTMLElement;
                setIsHiddenSlideOne(false);
                // only run logic if a different slide is now active

                if (newActive !== currentActive.current) {
                    // remove .active from all slides
                    setActiveSlide(newActive?.dataset.bg === "bg1" ? 1 :
                        newActive?.dataset.bg === "bg2" ? 2 :
                        newActive?.dataset.bg === "bg3" ? 3 :
                        newActive?.dataset.bg === "bg4" ? 4 :
                        newActive?.dataset.bg === "bg5" ? 5 : 0);

                    slidesForObserver.forEach((el) => el.classList.remove(styles.active));

                    // add .active to the new one
                    newActive.classList.add(styles.active);

                    // update current active
                    currentActive.current = newActive;

                    // ✅ call your debounced Lenis scroll handler here
                    onLenisScroll(newActive);
                }
            } else {
                // if no slides visible, remove active classes
                // slidesForObserver.forEach((el) => el.classList.remove(styles.active));
                // currentActive.current = null;
            }
            // [0.2, 0.5, 0.9]
        }, { threshold: [ interactionthreshold3.current
        ], root: null, rootMargin: `${marginpx.current}px` });


        slidesForObserver.forEach((s) => observer.observe(s));

        return () => {
            mounted = false;
            observer.disconnect();
            const box = document.querySelector('.scrollable');
            // hide scrollbar
            box?.classList.add('hide-scrollbar !important');

            window.removeEventListener("resize", handleResize);
            if (rafId.current) cancelAnimationFrame(rafId.current);
            if (lenisRef.current) {
                try {
                    if (typeof lenisRef.current.off === "function" && lenisScrollHandler) lenisRef.current.off("scroll", lenisScrollHandler);
                    if (typeof lenisRef.current.destroy === "function") lenisRef.current.destroy();
                } catch (e) { /* ignore */ }
                lenisRef.current = null;
            }
            if (snapTimeout) window.clearTimeout(snapTimeout);
        };
    }, []);
    const navHome = () => {
        const element = document.getElementById("kgnfLoginCont") as HTMLElement;
        setActiveSlide(0);
        if (lenisRef.current && element) {
            lenisRef.current.scrollTo(element, {
                offset: -window.innerHeight / 2 + element.clientHeight / 2,
                duration: 0.5,
                lock: true,
                easing: "easeInOut"
            });
        }
    };
    return (
        <section ref={containerRef} className={styles.wrapper} aria-label="Autonomous Accounts Payable">
            {/* slide 1: title + one-sentence lead (storybook first slide) */}
            <div className={styles.slide} data-bg="bg1">
                <div className={styles.centerContent}>
                    <h2 className={styles.title}>Trailblazing the way for autonomous process handling.</h2>
                </div>
                <h3 className={styles.subtitle}>Kognify Accounts Payable</h3>
                <ScrollDownAnimatedArrows contClassName="aquaArrows"/>
            </div>


            {/* slide 2: Big question left, Modern Systems + three huge elements right */}
            <div className={[styles.slide, styles.semiSplit].join(" ")} data-bg="bg2">
                <div className={styles.halfLeft}>
                    <blockquote className={styles.bigQuestion}>
                    How fast would your business move if 99% of invoices cleared themselves in 60 seconds?
                        <div className={styles.sectionSubSub}/>
                    </blockquote>

                </div>
                <div className={styles.halfRight}>
                    <div className={styles.sectionSubSubSub}>Today <span className={styles.textColorHighlight}>~30%</span> of AP is automated with success rate of <span className={styles.textColorHighlight}>~60%</span>.
                            But even when succesfull, <span className={styles.textColorHighlight}>all</span> automation steps demand manual verification and approval. Why?
                    </div>
                    <div className={styles.bigGrid}>
                        <div className={styles.bigCard} style={{ ["--i" as any]: "0s" }}>
                                <div className={styles.bigCardTopRightTag}>Issue</div>
                                <div className={styles.cardHeading}>Stop double work</div>
                                <div className={styles.cardBody}>Current AP systems lack logical understanding about the process & data, relegating them to glorified form prefiling tools. All mental work is redone manually to verify results.</div>

                        </div>
                        <div className={styles.bigCard} style={{ ["--i" as any]: "0.12s" }}>
                            <div className={styles.bigCardTopRightTag}>Issue</div>
                            <div className={styles.cardHeading}>Stop waiting days for approvals</div>
                                <div className={styles.cardBody}>Without bullet proof autonomous verification, current systems are reliant on manual verification, reviews and approval, each taking significant coordination & effort.</div>
                        </div>
                        <div className={styles.bigCard} style={{ ["--i" as any]: "0.24s" }}>
                        <div className={styles.bigCardTopRightTag}>Issue</div>
                            <div className={styles.cardHeading}>Stop tiring your people with menial work</div>
                            <div className={styles.cardBody}>Systems don't reliably assess accuracy, distinguish genuine from system errors or provide reasoning for their results boggling down people with complex failures to menial tasks.</div>
                        </div>
                    </div>
                </div>
                <ScrollDownAnimatedArrows contClassName="whiteArrows"/>
            </div>



            {/* slide 3: huge impactful quote + "unlike current systems" and steps + abilities showcase */}
            <div className={[styles.slide, styles.combined].join(" ")} data-bg="bg3">
                <div className={styles.combinedLeft}>
                    <blockquote className={styles.hugeImpactCombined}>
                        Accounting Teams need accountability, precision and clarity - not more complexity.
                    </blockquote>
                </div>

                <div className={styles.combinedRight}>

                    <div className={styles.sectionSubSubSub}>Kognify AAP is autonomous, accountable & precise. It does full recalculation, verification & self-assessment for 99% accurate results.</div>
                    <div className={styles.stepsWrap}>
                        <div className={styles.bigCardTwo} style={{ ["--i" as any]: "0.12s" }}>

                            <div className={styles.cardHeading}>Build logical understanding</div>
                            <div className={styles.cardBody}>Kognify builds logical understanding about the data & the process and considers your instructions and special requirements.</div>
                        </div>
                        <div className={styles.bigCardTwo} style={{ ["--i" as any]: "0.12s" }}>

                            <div className={styles.cardHeading}>Fully review and verify</div>
                            <div className={styles.cardBody}>Kognify reviews and fully recalculates the invoice from scratch. In case of discrepancies it determines causes, takes corrective actions and provides explanations.</div>
                        </div>
                        <div className={styles.bigCardTwo} style={{ ["--i" as any]: "0.12s" }}>

                            <div className={styles.cardHeading}>Align, Match, Reconcile</div>
                            <div className={styles.cardBody}>Kognify aligns with external data, matches & verifies receipts/POs. summarizes or regroups results as needed and provides reports with context and reasoning.</div>
                        </div>
                        <div className={styles.bigCardTwo} style={{ ["--i" as any]: "0.12s" }}>

                            <div className={styles.cardHeading}>Configurable & Adapt</div>
                            <div className={styles.cardBody}>Kognify is onboarded with written instructions, similarly to an employee. This approach allows quick and effortless changes and wide applicability across your processes.</div>
                        </div>
                    </div>
                </div>
                <ScrollDownAnimatedArrows contClassName="darkBlueArrows"/>
            </div>


            {/* slide 4: We deliver (very large showcase of two points) */}
            <div className={[styles.slide, styles.deliverSlide].join(" ")} data-bg="bg4">
                <div className={styles.deliverGrid}>
                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>Instant Accounting</div>
                        <div className={styles.deliverText}>The fastest AP completes an invoice in +2 days. Bring that down to minutes and <span className={styles.textColorHighlight}>be aware of expenses and availability in real-time.</span></div>
                    </div>
                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>+99% Success Rate</div>
                        <div className={styles.deliverText}>Unmatched result quality with proven <span className={styles.textColorHighlight}>+99% success rate</span>.</div>
                    </div>

                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>Trust-First Autonomy</div>
                        <div className={styles.deliverText}>Full logical data verification and extensive self-assessment ensure <span className={styles.textColorHighlight}>discrepancies don't go unnoticed</span>. Kognify will earn your trust.</div>
                    </div>

                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>Results with reason</div>
                        <div className={styles.deliverText}>Kognify knows why data is correct or incorrect, and provides clear insights and details. <span className={styles.textColorHighlight}>No more mental gymnastics to understand results</span>.</div>
                    </div>

                    <div className={styles.deliverCard}>
                        <div className={styles.deliverNumber}>Changes without IT</div>
                        <div className={styles.deliverText}>Our unique configuration approach enables changes that snowballed into multi-month IT projects to be <span className={styles.textColorHighlight}>done by your accountant in a day.</span></div>
                    </div>
                </div>

                    <ScrollDownAnimatedArrows contClassName="greenArrows"/>

            </div>


            {/* slide 5:Try */}
            <div className={[styles.slide, styles.deliverSlide].join(" ")} data-bg="bg5">
                <div className={styles.tryGrid}>
                    <div className={styles.demoDescr}>Kognify will process and verify your invoice.</div>
                    <div className={styles.tryCard} onClick={() => {window.location.href=`${window.location.origin}?redeemCode=ipademo_bcd6b981-200b-4496-842f-9ec22955bf08_5DlSp&spt=ipademo&A=1&lib=1`}}>
                        <div className={styles.deliverNumber}>Upload Invoice</div>
                        <div className={styles.tryText}>Try <strong>instantly</strong> without registration</div>
                    </div>
                </div>
            </div>
            <div className={styles.decor + (isHiddenSlideOne == true ? ` ${styles.decorHidden}` : '')} aria-hidden="true" />
            {hitTop != true && (
                <SlideNav
                    activeSlide={activeSlide}
                    activeStoryId="aap"
                    navHome={() => {navHome()}}
                    callSlideid={(element, slideIndex) => {
                        setActiveSlide(slideIndex);
                        if (lenisRef.current) {
                            lenisRef.current.scrollTo(element, {
                                offset: -window.innerHeight / 2 + element.clientHeight / 2,
                                duration: 0.5,
                                lock: true,
                                easing: "easeInOut"
                            });
                        }
                    }}
                />
            )}
        </section>
    );
};

export default AutonomousAP;
